import { z } from "zod";

export const taskRunStatusSchema = z
  .enum(["pending", "running", "succeeded", "failed"])
  .describe("TaskRunStatus");

export const taskActionPayloadSchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("agent"),
      prompt: z.string().trim().min(1),
    }),
  ])
  .describe("TaskActionPayload");

export const taskSchema = z
  .object({
    id: z.uuid(),
    projectId: z.uuid(),
    createdByUserId: z.uuid(),
    title: z.string(),
    emoji: z.string().trim().min(1).max(8).default("✨"),
    actionPayload: taskActionPayloadSchema,
    rrule: z.string(),
    nextRunAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .describe("Task");

export const taskRunSchema = z
  .object({
    id: z.uuid(),
    taskId: z.uuid(),
    projectId: z.uuid(),
    scheduledFor: z.string(),
    startedAt: z.string().nullable(),
    completedAt: z.string().nullable(),
    status: taskRunStatusSchema,
    actionPayloadSnapshot: taskActionPayloadSchema,
    sessionId: z.uuid().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .describe("TaskRun");

export const taskRunWithTaskSchema = taskRunSchema
  .extend({
    taskEmoji: z.string().trim().min(1).max(8).default("✨"),
    taskTitle: z.string(),
  })
  .describe("TaskRunWithTask");

export const taskRunQueueMessageSchema = z
  .object({
    type: z.literal("task.run"),
    taskId: z.uuid(),
    scheduledFor: z.string(),
    actionPayloadSnapshot: taskActionPayloadSchema,
  })
  .describe("TaskRunQueueMessage");

export const taskQueueMessageSchema =
  taskRunQueueMessageSchema.describe("TaskQueueMessage");

export const updateTaskInputSchema = z
  .object({
    title: z.string().trim().min(1).max(255),
    emoji: z.string().trim().min(1).max(8).optional(),
    prompt: z.string().trim().min(1),
    scheduledAt: z.iso.datetime({ offset: true }),
    rrule: z.string().trim().min(1),
  })
  .describe("UpdateTaskInput");

export const createTaskInputSchema = updateTaskInputSchema
  .extend({
    projectId: z.uuid(),
  })
  .describe("CreateTaskInput");

export const taskResponseSchema = z
  .object({
    object: z.literal("task"),
    id: z.uuid(),
    title: z.string(),
    emoji: z.string(),
    rrule: z.string(),
    nextRunAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .meta({ id: "Task" });

export type Task = z.infer<typeof taskSchema>;
export type TaskRun = z.infer<typeof taskRunSchema>;
export type TaskRunWithTask = z.infer<typeof taskRunWithTaskSchema>;
export type TaskRunStatus = z.infer<typeof taskRunStatusSchema>;
export type TaskActionPayload = z.infer<typeof taskActionPayloadSchema>;
export type TaskRunQueueMessage = z.infer<typeof taskRunQueueMessageSchema>;
export type TaskQueueMessage = z.infer<typeof taskQueueMessageSchema>;
export type UpdateTaskInput = z.infer<typeof updateTaskInputSchema>;
export type CreateTaskInput = z.infer<typeof createTaskInputSchema>;
export type TaskResponse = z.infer<typeof taskResponseSchema>;
